import React, { useState, useMemo } from "react"
import Icon from "../AppIcon"
import Button from "../ui/Button"
import { proposeAppointment } from "../../utils/mentorshipHelpers"

// Fixed working-hours window for v1 — 9:00 AM to 5:00 PM in 30-minute
// increments. Not availability-aware yet; that's a natural follow-on once
// mentors want to set their own hours.
const START_HOUR = 9
const END_HOUR = 17
const STEP_MINUTES = 30

function buildDayOptions() {
  const days = []
  const now = new Date()
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i)
    const label = i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { weekday: "short" })
    days.push({ offset: i, date: d, label, dayNum: d.getDate() })
  }
  return days
}

function buildSlotsForDay(date) {
  const slots = []
  for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += STEP_MINUTES) {
      const slot = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m)
      slots.push(slot)
    }
  }
  return slots
}

function formatSlotTime(d) {
  let hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12
  if (hours === 0) hours = 12
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`
  return `${hours}:${minutesStr} ${ampm}`
}

// existingAppointments: appointments already loaded for this mentorship,
// used to grey out slots that already have a live (non-declined/cancelled)
// proposal at that exact time.
const AppointmentScheduler = ({ orgId, mentorshipId, currentUser, existingAppointments, onClose, onProposed }) => {
  const days = useMemo(() => buildDayOptions(), [])
  const [dayOffset, setDayOffset] = useState(0)
  const [selectedSlotMs, setSelectedSlotMs] = useState(null)
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const selectedDay = days[dayOffset]
  const slots = useMemo(() => buildSlotsForDay(selectedDay.date), [selectedDay])

  const takenTimes = useMemo(() => {
    const set = new Set()
    ;(existingAppointments || []).forEach((a) => {
      if (a.status === "proposed" || a.status === "confirmed") set.add(a.scheduledForMs)
    })
    return set
  }, [existingAppointments])

  const handlePropose = async () => {
    if (!selectedSlotMs || submitting) return
    setSubmitting(true)
    try {
      await proposeAppointment(orgId, mentorshipId, {
        proposedByUid: currentUser.uid,
        scheduledForMs: selectedSlotMs,
        note: note.trim(),
      })
      onProposed?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Propose a Time</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted">
          <Icon name="X" size={16} className="text-muted-foreground" />
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {days.map((d) => (
          <button
            key={d.offset}
            onClick={() => { setDayOffset(d.offset); setSelectedSlotMs(null) }}
            className={`flex-shrink-0 flex flex-col items-center justify-center w-14 h-16 rounded-xl border transition-smooth ${
              dayOffset === d.offset
                ? "bg-accent text-white border-accent"
                : "bg-background text-foreground border-border hover:bg-muted"
            }`}
          >
            <span className="text-[10px] font-semibold uppercase">{d.label}</span>
            <span className="text-base font-bold mt-0.5">{d.dayNum}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
        {slots.map((slot) => {
          const ms = slot.getTime()
          const taken = takenTimes.has(ms)
          const isPast = ms < Date.now()
          const disabled = taken || isPast
          return (
            <button
              key={ms}
              disabled={disabled}
              onClick={() => setSelectedSlotMs(ms)}
              className={`text-xs font-medium rounded-lg px-2 py-2 border transition-smooth ${
                disabled
                  ? "bg-muted text-muted-foreground border-border opacity-40 cursor-not-allowed"
                  : selectedSlotMs === ms
                    ? "bg-accent text-white border-accent"
                    : "bg-background text-foreground border-border hover:bg-muted"
              }`}
            >
              {formatSlotTime(slot)}
            </button>
          )
        })}
      </div>

      <input
        value={note}
        onChange={(e) => setNote(e?.target?.value)}
        placeholder="What's this session about? (optional)"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />

      <Button
        variant="default"
        size="sm"
        onClick={handlePropose}
        disabled={!selectedSlotMs || submitting}
      >
        {submitting ? "Sending…" : "Send Proposal"}
      </Button>
    </div>
  )
}

export default AppointmentScheduler